import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SuccessModal from "./SuccessModal";

declare global {
  interface Window {
    PaystackPop: any;
  }
}

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess: (response: any) => Promise<void>;
  userEmail: string;
}

interface PaystackResponse {
  reference: string;
  status: string;
  trans: string;
  transaction: string;
  message: string;
}

export const PaywallModal = ({ isOpen, onClose, onPaymentSuccess, userEmail }: PaywallModalProps) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isPaystackReady, setIsPaystackReady] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isPaystackModalOpen, setIsPaystackModalOpen] = useState(false);

  // Add a check for premium status
  const isPremiumUser = localStorage.getItem('isPremiumUser') === 'true';

  // Add useEffect to handle premium status check
  useEffect(() => {
    if (isPremiumUser) {
      onClose(); // Close modal if user is premium
    }
  }, [isPremiumUser, onClose]);

  useEffect(() => {
    // Check if Paystack is loaded
    const checkPaystack = () => {
      if (window.PaystackPop) {
        setIsPaystackReady(true);
        return;
      }
      setTimeout(checkPaystack, 500);
    };
    checkPaystack();
  }, []);

  const handlePayment = () => {
    if (!isPaystackReady) {
      console.error('Paystack is not ready');
      return;
    }

    setIsPaystackModalOpen(true); // Set Paystack modal as open
    
    try {
      const handler = window.PaystackPop.setup({
        key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
        email: userEmail,
        amount: 200000, // Amount in kobo (₦2000 = 200000 kobo)
        currency: 'NGN',
        ref: `jamb_${Math.floor(Math.random() * 1000000000)}`,
        onClose: function() {
          setIsPaystackModalOpen(false); // Reset when Paystack modal closes
          onClose();
        },
        callback: function(response: PaystackResponse) {
          setIsVerifying(true);
          setIsPaystackModalOpen(false); // Reset when payment is complete
          onPaymentSuccess({
            reference: response.reference,
            status: response.status,
            timestamp: new Date().toISOString(),
            amount: 2000,
            userEmail
          }).then(() => {
            setShowSuccessModal(true); // Show success modal after successful payment
          })
          .catch((error) => {
            console.error('Payment verification failed:', error);
          })
          .finally(() => {
            setIsVerifying(false);
            onClose();
          });
        }
      });
      
      handler.openIframe();
    } catch (error) {
      console.error('Error initializing Paystack:', error);
      setIsPaystackModalOpen(false);
    }
  };

  return (
    <>
      {/* Only show the Dialog when Paystack modal is not open */}
      {!isPaystackModalOpen && (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Upgrade to Premium</DialogTitle>
              <DialogDescription>
                Get unlimited access to all questions across all years for a one-time payment of ₦2,000.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-purple-50 p-4 rounded-lg mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-700 mb-1">₦2,000</div>
                <div className="text-sm text-purple-600">One-time payment for lifetime access</div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">Premium features include:</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Access to all past questions
                </li>
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  All subjects and years available
                </li>
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Detailed explanations
                </li>
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Progress tracking
                </li>
              </ul>
            </div>

            <div className="flex gap-4 mt-4">
              <button
                onClick={onClose}
                disabled={isVerifying}
                className="flex-1 px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                Maybe Later
              </button>
              <button
                onClick={handlePayment}
                disabled={isVerifying}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {isVerifying ? 'Verifying...' : 'Pay ₦2,000'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <SuccessModal 
        isOpen={showSuccessModal} 
        onClose={() => {
          setShowSuccessModal(false);
          onClose();
        }} 
      />
    </>
  );
};

export default PaywallModal; 